/* eslint-disable no-else-return */
/* eslint-disable no-lonely-if */
/* eslint-disable no-nested-ternary */
import { mapValues, remove, cloneDeep } from 'lodash';
import {
  checkSubqchoices,
  isSubqchoice,
  updateStructure,
} from '@mummssoftware/utils/form-engine/form-engine-utils';
import { hasFormSubmission } from '@mummssoftware/utils/form-engine/form-engine-archives';
import { statement, radio, checkbox, isonerequired } from './constants';

/**
 * FAJITA overlay tokens that must stay quoted string values.
 * cleanFormSubmission unwraps `"{` / `}"` for Map Pain; that breaks
 * `"questionlabel":"{other-non-covered}"` into invalid JSON until observation
 * substitutes the token.
 *
 * TODO(OP-33118): DELETE this placeholder protection (and restore the simple
 * cleanFormSubmission one-liner) when observation overlayjsonparams replaces
 * `{other-non-covered}` at form GET time.
 */
const FAJITA_OVERLAY_PLACEHOLDERS = [
  '{other-non-covered}',
  '{meds}',
  // before `{dx}` — `{dx}` is a prefix of `{dx-unrelated}`
  '{dx-unrelated}',
  '{dx}',
  '{unrelated-dx}',
  '{patientname}',
  '{agencyname}',
  '{patientnumber}',
];

/**
 * cleanFormSubmission for Map Pain issue
 */
export const cleanFormSubmission = (form: any) => {
  if (typeof form !== 'string') {
    return form;
  }

  const sentinelByToken = FAJITA_OVERLAY_PLACEHOLDERS.map((token, index) => ({
    token,
    sentinel: `__FAJITA_PLACEHOLDER_${index}__`,
  }));

  let protectedForm = form;
  sentinelByToken.forEach(({ token, sentinel }) => {
    protectedForm = protectedForm.split(token).join(sentinel);
  });

  let cleaned = protectedForm
    .replaceAll('\\', '')
    .replaceAll('"{', '{')
    .replaceAll('}"', '}');

  sentinelByToken.forEach(({ token, sentinel }) => {
    cleaned = cleaned.split(sentinel).join(token);
  });

  return cleaned;
};

/**
 * create form data
 */
export const createFormData = (formData: any) => {
  let formdata: any = null;

  const autoSaveState = formData?.formsubmissionautosave;
  const previousSubmission = hasFormSubmission(formData)
    ? formData.formsubmission
    : null;
  // autoSaveState is submitState (new autosave solution) or a string (old saving)
  if (autoSaveState) {
    // previous autosave send entire payload
    const isPreviousAutoSave = Array.isArray(JSON.parse(autoSaveState));
    if (!isPreviousAutoSave) {
      // if fallback data
      if (previousSubmission) {
        formdata = createAutoSaveForm(previousSubmission, autoSaveState);
      } else {
        formdata = null;
      }
    } else {
      formdata =
        formData?.formdefinition ||
        formData?.formsubmissionautosave ||
        formData?.formsubmission;
    }
  } else {
    formdata =
      formData?.formdefinition ||
      formData?.formsubmissionautosave ||
      formData?.formsubmission;
  }
  /**
   * cleanFormSubmission for Map Pain issue
   */

  if (formdata && formdata !== '[null]') {
    const cleanFormData = cleanFormSubmission(formdata);
    const stringifyData = JSON.stringify(cleanFormData);

    let parsedData: any = cleanFormData;
    try {
      parsedData = stringifyData && JSON.parse(cleanFormData);
    } catch (e) {
      // catch JSON parse exceptions
      console.log('JSON PARSING ERROR', e);
    }

    const correctdata =
      parsedData && Array.isArray(parsedData) ? parsedData : null;

    if (correctdata) {
      const data = updateStructure(parsedData);
      return data;
    }
    return null;
  }
  return null;
};
/**
 * renderTreeStructure - NOT USED
 */

export const renderTreeStructure = (inputs: any, depthLevel = 0) => {
  const menuTree = inputs.reduce((seed: any, val: any) => {
    const hasOptions = checkSubqchoices(val);

    let subMenu;
    if (hasOptions) {
      const newDepthLevel = depthLevel + 1;

      subMenu = renderTreeStructure(val.subqchoices, newDepthLevel);
      seed[val.id] = subMenu;
    } else {
      seed[val.id] = {};
    }
    return seed;
  }, {} as any);

  return menuTree;
};

/**
 * creates initGroupBoxStateData
 */

export const createInitState = (data: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        if (val.questionpath) {
          if (seed[val.parentquestionpath]) {
            seed[val.parentquestionpath][val.questionpath] = false;
          } else {
            seed[val.parentquestionpath] = {
              [val.questionpath]: false,
            };
          }
        }
        return seed;
      }, {} as Record<string, Record<string, boolean>>)
    : null;

/**
 * creates initEditGroupBoxStateData for the editing version
 */

export const createEditInitState = (data: any, initseed: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        // hasValue is a boolean
        const hasValue =
          isSubqchoice(val) ||
          (typeof val.value === 'string' && val.value !== 'false');
        if (hasValue) {
          // value for text input
          const value = isSubqchoice(val) ? true : val.value;
          const isText = !isSubqchoice(val);
          if (seed[val.parentquestionpath]) {
            seed[val.parentquestionpath][val.questionpath] = value;
            if (isText) {
              seed[val.questionpath] = value;
            } else {
              seed[val.questionpath] = {};
            }
            if (checkSubqchoices(val)) {
              createEditInitState(val.subqchoices, seed);
            }
          } else {
            seed[val.parentquestionpath] = {
              [val.questionpath]: value,
            };
            seed[val.questionpath] = {};
            if (checkSubqchoices(val)) {
              createEditInitState(val.subqchoices, seed);
            }
          }
        }
        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGolbalEditInitState = (data: any) =>
  createEditInitState(data, {});

const createControlTypeState = (data: any, initseed: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        seed[val.questionpath] = val.controltype;
        if (checkSubqchoices(val)) {
          createControlTypeState(val.subqchoices, seed);
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;
export const createGlobalTypeState = (data: any) =>
  createControlTypeState(data, {});
/**
 * FORMS SPECIFIC REQUIRED QUESTIONS
 */
/**
 * creates Parent Global Required State
 * NOT USED
 */

const createParentRequiredState = (data: any, initseed: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        // required is a boolean
        const { required } = val;
        if (required) {
          seed[val.questionpath] = true;
        } else if (checkSubqchoices(val)) {
          createParentRequiredState(val.subqchoices, seed);
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

/**
 * creates Parent Global Number Required Questions State
 */

const createParentRequiredQuestState = (data: any, initseed: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        // required is a boolean
        const { required } = val;
        if (required) {
          seed[val.questionpath] = true;
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createParentGlobalRequiredQuestState = (data: any) =>
  createParentRequiredQuestState(data, {});

/**
 * creates Parent Disabled Question State
 */

const createParentDisabledState = (data: any, initseed: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        // dis is a boolean
        const { dis } = val;
        if (dis) {
          seed[val.questionpath] = true;
        } else if (checkSubqchoices(val)) {
          createParentDisabledState(val.subqchoices, seed);
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createParentGlobalDisabledState = (data: any) =>
  createParentDisabledState(data, {});

type RequiredPath = {
  controltype: string;
  parent: string;
  rule: string | null;
  subqchoices?: any;
};

const checkAllChildrenRadio = (el: any[]) =>
  el.every((element) => isRadioOrCheck(element));

// radio or checkboxes elements that are not required but have required children if selected
const createNonRequiredState = (data: any, initseed: any) =>
  data && data.length
    ? data.reduce((seed: any, val: any) => {
        if (val) {
          // required is a boolean
          const { required } = val;
          if (isRadioOrCheck(val)) {
            if (checkSubqchoices(val)) {
              if (!required) {
                // children are ALL radio buttons or checkboxes
                if (checkAllChildrenRadio(val.subqchoices)) {
                  createNonRequiredState(val.subqchoices, seed);
                  // if children are not radio and required
                } else if (val.subqchoices[0].required) {
                  seed[val.questionpath] = {
                    controltype: val.controltype,
                    parent: val.parentquestionpath,
                    rule: val.rule,
                    subqchoices: val.subqchoices,
                  };
                }
              } else {
                createNonRequiredState(val.subqchoices, seed);
              }
            }
          } else if (checkSubqchoices(val)) {
            createNonRequiredState(val.subqchoices, seed);
          }
        }
        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;
const createGlobalNonRequiredState = (data: any) =>
  createNonRequiredState(data, {});

/**
 * creates Global LEVEL 0 RADIO Required State
 */

const createRequiredLevel0State = (data: any, initseed: any) =>
  data && data.length
    ? data.reduce((seed: any, val: any) => {
        if (val) {
          // required is a boolean
          const { required } = val;
          if (isRadioOrCheck(val)) {
            if (required) {
              if (!seed[val.parentquestionpath]) {
                seed[val.parentquestionpath] = {};
                seed[val.parentquestionpath][val.questionpath] = {};
              }
            }
          } else {
            if (required) {
              if (checkSubqchoices(val)) {
                createRequiredLevel0State(val.subqchoices, seed);
              }
              // if first value not required go down the tree
            } else if (checkSubqchoices(val)) {
              createRequiredLevel0State(val.subqchoices, seed);
            }
          }
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGlobalLevel0RequiredState = (data: any) =>
  createRequiredLevel0State(data, {});
/**
 * creates Global PARENT CONTROL Required State
 */

const createRequiredParentControlState = (
  data: any,
  isEl: (x: any) => any,
  initseed: any,
) =>
  data && data.length
    ? data.reduce((seed: any, val: any) => {
        if (val) {
          // required is a boolean
          const { required } = val;
          if (isEl(val)) {
            if (required) {
              if (!seed[val.parentquestionpath]) {
                seed[val.parentquestionpath] = {};
                seed[val.parentquestionpath][val.questionpath] = {};
              }
            }
          } else {
            if (required) {
              if (checkSubqchoices(val)) {
                createRequiredParentControlState(val.subqchoices, isEl, seed);
              }
              // if first value not required go down the tree
            } else if (checkSubqchoices(val)) {
              createRequiredParentControlState(val.subqchoices, isEl, seed);
            }
          }
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGlobalParentControlRequiredState = (
  data: any,
  isEl: (x: any) => any,
) => createRequiredParentControlState(data, isEl, {});
/**
 * creates Global PARENT RADIO Required State
 */

const createRequiredParentRadioState = (data: any, initseed: any) =>
  data && data.length
    ? data.reduce((seed: any, val: any) => {
        if (val) {
          // required is a boolean
          const { required } = val;
          if (isRadio(val)) {
            if (required) {
              if (!seed[val.parentquestionpath]) {
                seed[val.parentquestionpath] = {};
                seed[val.parentquestionpath][val.questionpath] = {};
              }
            }
          } else {
            if (required) {
              if (checkSubqchoices(val)) {
                createRequiredParentRadioState(val.subqchoices, seed);
              }
              // if first value not required go down the tree
            } else if (checkSubqchoices(val)) {
              createRequiredParentRadioState(val.subqchoices, seed);
            }
          }
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGlobalParentRadioRequiredState = (data: any) =>
  createRequiredParentRadioState(data, {});
/**
 * creates Global CONTROL Required State
 */

const createRequiredControlState = (
  data: any,
  isEl: (x: any) => any,
  initseed: any,
) =>
  data && data.length
    ? data.reduce((seed: any, val: any) => {
        if (val) {
          // required is a boolean
          const { required } = val;
          if (isEl(val)) {
            if (required) {
              if (seed[val.parentquestionpath]) {
                seed[val.parentquestionpath][val.questionpath] = {};
              } else {
                seed[val.parentquestionpath] = {};
                seed[val.parentquestionpath][val.questionpath] = {};
              }
              if (checkSubqchoices(val)) {
                createRequiredControlState(val.subqchoices, isEl, seed);
              }
            }
            if (checkSubqchoices(val)) {
              createRequiredControlState(val.subqchoices, isEl, seed);
            }
          } else {
            if (required) {
              if (checkSubqchoices(val)) {
                createRequiredControlState(val.subqchoices, isEl, seed);
              }
              // if first value not required go down the tree
            } else if (checkSubqchoices(val)) {
              createRequiredControlState(val.subqchoices, isEl, seed);
            }
          }
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGlobalControlRequiredState = (
  data: any,
  isEl: (x: any) => any,
) => createRequiredControlState(data, isEl, {});
/**
 * creates Global RADIO Required State
 */

const createRequiredRadioState = (data: any, initseed: any) =>
  data && data.length
    ? data.reduce((seed: any, val: any) => {
        if (val) {
          // required is a boolean
          const { required } = val;
          if (isRadio(val)) {
            if (required) {
              if (seed[val.parentquestionpath]) {
                seed[val.parentquestionpath][val.questionpath] = {};
              } else {
                seed[val.parentquestionpath] = {};
                seed[val.parentquestionpath][val.questionpath] = {};
              }
              if (checkSubqchoices(val)) {
                createRequiredRadioState(val.subqchoices, seed);
              }
            }
            if (checkSubqchoices(val)) {
              createRequiredRadioState(val.subqchoices, seed);
            }
          } else {
            if (required) {
              if (checkSubqchoices(val)) {
                createRequiredRadioState(val.subqchoices, seed);
              }
              // if first value not required go down the tree
            } else if (checkSubqchoices(val)) {
              createRequiredRadioState(val.subqchoices, seed);
            }
          }
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGlobalRadioRequiredState = (data: any) =>
  createRequiredRadioState(data, {});

/**
 * creates Global Required State
 */

const createControlState = (data: any, initseed: any) =>
  data && data.length
    ? data.reduce((seed: any, val: any) => {
        if (val) {
          // required is a boolean
          const { required } = val;
          if (required) {
            if (seed[val.parentquestionpath]) {
              seed[val.parentquestionpath][val.questionpath] = val.controltype;
            } else {
              seed[val.parentquestionpath] = {};
              seed[val.parentquestionpath][val.questionpath] = val.controltype;
            }
            if (checkSubqchoices(val)) {
              createControlState(val.subqchoices, seed);
            }
          } else {
            if (checkSubqchoices(val)) {
              createControlState(val.subqchoices, seed);
            }
          }
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

// NOT USED
const createGlobalControlState = (data: any) => createControlState(data, {});
/**
 * creates Global Required State
 */

const createRequiredState = (data: any, initseed: any) =>
  data && data.length
    ? data.reduce((seed: any, val: any) => {
        if (val) {
          // required is a boolean
          const { required } = val;
          if (isRadioOrCheck(val)) {
            if (required) {
              seed[val.questionpath] = {
                controltype: val.controltype,
                parent: val.parentquestionpath,
                rule: val.rule,
                subqchoices: val.subqchoices,
              };
            }
            // todo
            // if (checkSubqchoices(val)) {
            //   createRequiredState(val.subqchoices, seed);
            // }
          } else {
            if (required) {
              seed[val.questionpath] = {
                controltype: val.controltype,
                parent: val.parentquestionpath,
                rule: val.rule,
                subqchoices: val.subqchoices,
              };

              if (checkSubqchoices(val)) {
                createRequiredState(val.subqchoices, seed);
              }
              // if first value not required go down the tree
            } else if (checkSubqchoices(val)) {
              createRequiredState(val.subqchoices, seed);
            }
          }
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

const createGlobalRequiredState = (data: any) => createRequiredState(data, {});

/**
 * check Disabled State
 */

const buildUpdatedReqState = (
  data: any,
  disState?: Record<string, boolean>,
  reqState?: any,
) => {
  // if disState[x] is true then it is non required
  // disabled means non required

  // if required exists && disabled exists
  if (reqState && Object.keys(reqState).length) {
    if (disState && Object.keys(disState).length) {
      // 2. check on the non required state
      const nonRequiredArr: string[] = [];
      const disKeys = Object.keys(disState);
      if (disKeys.length) {
        disKeys.forEach((x: string) => {
          if (disState[x]) {
            nonRequiredArr.push(x);
          }
        });
      }
      // if there are disabled questions
      if (nonRequiredArr.length) {
        // 3. rebuild the required state
        const requiredKeys = Object.keys(reqState);
        nonRequiredArr.forEach((x: string) => {
          requiredKeys.forEach((key: string) => {
            if (key.includes(x)) {
              delete reqState[key];
            }
          });
        });
      }
    }
  }
  return reqState;
};

/**
 * check Required State
 */

const isCheckboxRequired = (el: any) =>
  el.controltype === checkbox && el.rule === isonerequired;

const isRadioOrCheck = (el: any) =>
  el.controltype === radio || el.controltype === checkbox;

const isRadio = (el: any) => el.controltype === radio;

const isLabelRadio = (el: any) => el.controltype === 'label';

const isRadioOrCheckBox = (el: any) =>
  isRadio(el) || isCheckboxRequired(el) || isLabelRadio(el);

const handleControlCheckRecurssively = (
  radiopath: string,
  radioState: Record<string, any>,
  submitted: Record<string, boolean>,
  // eslint-disable-next-line consistent-return
): any => {
  if (radiopath) {
    const radiostate = radioState[radiopath];
    if (radiostate && Object.keys(radiostate).length) {
      if (submitted[radiopath]) {
        const radioEl = checkReqControlFunc(radiostate, submitted);
        if (radioEl) {
          return handleControlCheckRecurssively(radioEl, radioState, submitted);
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return true;
    }
  } else {
    return false;
  }
};

const checkReqControlFunc = (
  state: any,
  submitted: Record<string, boolean>,
) => {
  if (state && Object.keys(state).length) {
    const array = Object.keys(state);
    const radioEl = array.find((x: string) => submitted[x]);
    return radioEl;
  }
  return false;
};

const handleControlChildCheck = (
  array: any[],
  requiredRadio: Record<string, any>,
  submitted: Record<string, boolean>,
): any => {
  const { length } = array;

  for (let index = 0; index < length; index += 1) {
    const childKey = array[index];
    if (submitted[childKey]) {
      const checkEachRadioRec = handleControlCheckRecurssively(
        childKey,
        requiredRadio,
        submitted,
      );
      if (!checkEachRadioRec) {
        return false;
      }
    }
  }
  return true;
};
const handleControlParentCheck = (
  array: any[],
  requiredRadio: Record<string, any>,
  submitted: Record<string, boolean>,
): any => {
  const { length } = array;

  for (let index = 0; index < length; index += 1) {
    const parentKey = array[index];
    const checkEachRadioRec = handleControlCheckRecurssively(
      parentKey,
      requiredRadio,
      submitted,
    );
    if (!checkEachRadioRec) {
      return false;
    }
  }
  return true;
};

const checkControlBlock = (
  requiredParentRadio: Record<string, any>,
  requiredRadio: Record<string, any>,
  requiredLevel0Control: Record<string, any>,
  submitted: Record<string, boolean>,
) => {
  let checkRequiredRadio;
  // @note children radio element hidden behind conditional choices
  let checkRequiredChildRadio;
  // @note first REQUIRED RADIOS DOWN THE TREE
  if (
    requiredParentRadio &&
    Object.keys(requiredParentRadio).length &&
    requiredRadio &&
    Object.keys(requiredRadio).length
  ) {
    const parentRadioArr = Object.keys(requiredParentRadio);
    if (parentRadioArr.length) {
      const newParentArr = parentRadioArr.filter(
        (x: string) => requiredLevel0Control[x],
      );
      const newChildArr = parentRadioArr.filter(
        (x: string) => !requiredLevel0Control[x],
      );
      if (newParentArr.length) {
        const checkRadio = handleControlParentCheck(
          newParentArr,
          requiredRadio,
          submitted,
        );
        checkRequiredRadio = checkRadio;
      } else {
        checkRequiredRadio = true;
      }
      if (newChildArr.length) {
        const checkRadio = handleControlChildCheck(
          newChildArr,
          requiredRadio,
          submitted,
        );
        checkRequiredChildRadio = checkRadio;
      } else {
        checkRequiredChildRadio = true;
      }
    }
  } else {
    checkRequiredRadio = true;
    checkRequiredChildRadio = true;
  }
  return checkRequiredRadio && checkRequiredChildRadio;
};

// only checks for radio/checkboxes/others one layer deep
export const checkRequired = (
  data: any,
  submitted: Record<string, boolean>,
  disabled?: Record<string, boolean>,
  print?: boolean,
) => {
  if (data && data.length) {
    // try to go by who is going to fail first
    // console.log('**data**', data);
    let required: Record<string, any>;
    // radio and checkboxes
    let requiredLevel0Control: Record<string, any>;
    // radios
    let requiredRadio: Record<string, any>;
    let requiredParentRadio: Record<string, any>;
    // checkboxes
    let requiredCheckBox: Record<string, any>;
    let requiredParentCheckBox: Record<string, any>;

    // @note disabled if for INA PAIN section yes/no conditional {'893913->893925->894196': true}
    if (disabled) {
      const requiredInitState = createGlobalRequiredState(data);
      const requiredInitLevel0Control = createGlobalLevel0RequiredState(data);
      // radio
      const requiredInitRadio = createGlobalControlRequiredState(data, isRadio);
      // only required Parent radios state {}
      const requiredInitParentRadio = createGlobalParentControlRequiredState(
        data,
        isRadio,
      );
      // checkboxes
      const requiredInitCheckBox = createGlobalControlRequiredState(
        data,
        isRadioOrCheckBox,
      );
      const requiredInitParentCheckBox = createGlobalParentControlRequiredState(
        data,
        isRadioOrCheckBox,
      );
      required = buildUpdatedReqState(data, disabled, requiredInitState);
      requiredLevel0Control = buildUpdatedReqState(
        data,
        disabled,
        requiredInitLevel0Control,
      );
      requiredRadio = buildUpdatedReqState(data, disabled, requiredInitRadio);
      // only required Parent radios state {}
      requiredParentRadio = buildUpdatedReqState(
        data,
        disabled,
        requiredInitParentRadio,
      );
      requiredCheckBox = buildUpdatedReqState(
        data,
        disabled,
        requiredInitCheckBox,
      );
      // only required Parent radios state {}
      requiredParentCheckBox = buildUpdatedReqState(
        data,
        disabled,
        requiredInitParentCheckBox,
      );
    } else {
      required = createGlobalRequiredState(data);
      requiredRadio = createGlobalControlRequiredState(data, isRadio);
      // only required Parent radios state {}
      requiredParentRadio = createGlobalParentControlRequiredState(
        data,
        isRadio,
      );
      requiredLevel0Control = createGlobalLevel0RequiredState(data);
      requiredCheckBox = createGlobalControlRequiredState(
        data,
        isRadioOrCheckBox,
      );
      requiredParentCheckBox = createGlobalParentControlRequiredState(
        data,
        isRadioOrCheckBox,
      );
    }

    // all control types
    const requiredArr = required && Object.entries(required);

    // if there are ANY required control types
    if (requiredArr.length) {
      // @note 1. first REQUIRED RADIOS DOWN THE TREE

      const checkRadioFirst = checkControlBlock(
        requiredParentRadio,
        requiredRadio,
        requiredLevel0Control,
        submitted,
      );

      // console.log('##########################################');
      // console.log('requiredRadio', requiredRadio);
      // console.log('data', data);
      // console.log('requiredParentRadio', requiredParentRadio);
      // console.log('requiredParentCheckBox', requiredParentCheckBox);
      // console.log('requiredCheckBox', requiredCheckBox);

      // @note 2. check the checkbox types IF radios are OK
      if (checkRadioFirst) {
        const checkCheckBoxSecond = checkControlBlock(
          requiredParentCheckBox,
          requiredCheckBox,
          requiredLevel0Control,
          submitted,
        );

        // @note 3. check all other control types if checkbox rule OK

        if (checkCheckBoxSecond) {
          // other control types BUT radio and SHARED checkboxes
          const requiredKeys = requiredArr.reduce((seed: any, val: any) => {
            const path: string = val[0];
            const res: RequiredPath = val[1];
            const radioEl = res.controltype === radio;
            const sharedCheckbox = isCheckboxRequired(res);
            if (!radioEl && !sharedCheckbox) {
              seed.push(path);
            }
            return seed;
          }, []);

          const isSubKeySubmitted = (key: string) =>
            submitted[key] === true ||
            (typeof submitted[key] === 'string' &&
              (submitted[key] as any) !== '');

          const checkRequiredKeys = requiredKeys.every((i: any) =>
            isSubKeySubmitted(i),
          );
          // console.log('**requiredKeys**', requiredKeys);

          if (checkRequiredKeys) {
            // check non required state
            const nonRequired = data && createGlobalNonRequiredState(data);
            // console.log('**nonRequired**', nonRequired);

            const checkSubkeys = (key: string) => {
              // non required radio was checked and
              if (isSubKeySubmitted(key)) {
                // check there is length
                if (checkSubqchoices(nonRequired[key])) {
                  // there are subqchoices
                  const keyChoices = nonRequired[key].subqchoices;
                  const subRequired = keyChoices.filter((x: any) => x.required);
                  if (subRequired.length) {
                    const isOK: any = checkRequired(subRequired, submitted);
                    return isOK;
                  }
                  return true;
                }
                // there are no subqchoices
                return true;
              }
              return true;
            };

            if (Object.keys(nonRequired).length) {
              // if state check
              const checkNonRequired = Object.keys(
                nonRequired,
              ).every((key: string) => checkSubkeys(key));
              return checkNonRequired;
            }
            return true;
          }
        }
      }

      return false;
    }
    return true;
  }
  return true;
};
/**
 * END OF FORMS SPECIFIC REQUIRED QUESTIONS
 */

/**
 * creates GolbalDCInitState for the editing version
 */

const createDCInitState = (data: any, initseed: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        // hasDC is a boolean
        const hasDC = val.dcdate;
        if (hasDC) {
          seed[val.questionpath] = true;
        } else if (checkSubqchoices(val)) {
          createDCInitState(val.subqchoices, seed);
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGolbalDCInitState = (data: any) =>
  createDCInitState(data, {});

/**
 * creates createEditInitSubmitState for the editing version
 */

export const createEditInitSubmitState = (data: any) => {
  if (data && Object.keys(data).length) {
    const seed: any = {};
    Object.keys(data).forEach((key) => {
      if (typeof data[key] === 'string') {
        seed[key] = data[key];
      } else {
        seed[key] = true;
      }
    });
    return seed;
  }
  return null;
};

/**
 * creates createEditInitLabelState for the editing version
 */

export const createEditInitLabelState = (data: any) => {
  if (data && Object.keys(data).length) {
    const seed: any = {};
    Object.keys(data).forEach((key) => {
      seed[key] = { expanded: true };
    });
    return seed;
  }
  return null;
};

/**
 * creates initSubmitGroupBoxState
 */

export const createInitSubmitState = (data: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        if (val.questionpath) {
          seed[val.parentquestionpath] = false;
        }
        return seed;
      }, {} as Record<string, boolean>)
    : null;

/**
 * creates initial ID array for the editing version
 */

const createInitIds = (data: any, initseed: any, depthLevel = 0) =>
  data && Array.isArray(data)
    ? data.reduce((seed: any, val: any) => {
        // hasValue is a boolean
        const hasValue =
          isSubqchoice(val) ||
          (typeof val.value === 'string' && val.value !== 'false');
        if (hasValue) {
          if (seed[depthLevel]) {
            seed[depthLevel].push(val.questionpath);
          } else {
            seed[depthLevel] = [val.questionpath];
          }
          if (checkSubqchoices(val)) {
            const newDepthLevel = depthLevel + 1;
            createInitIds(val.subqchoices, seed, newDepthLevel);
          }
        }
        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGlobalInitIds = (data: any) => createInitIds(data, []);

/**
 * update an item with specific ID in a tree structure
 * returns the tree structure
 */

export const updateElement = (obj: any, questionpath: any, state: any): any =>
  Array.isArray(obj)
    ? obj.map((o) => updateElement(o, questionpath, state))
    : obj.questionpath === questionpath
    ? {
        ...obj,
        value: state[questionpath],
      } // found it and update value
    : {
        ...obj,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateElement(obj.subqchoices, questionpath, state),
            }
          : {}),
      };

export const updateElementBis = (
  obj: any,
  questionpath: any,
  state: any,
  // eslint-disable-next-line consistent-return
): any => {
  let newObj: any;
  if (Array.isArray(obj)) {
    newObj = obj.map((o) => updateElementBis(o, questionpath, state));
  } else {
    if (obj.questionpath === questionpath) {
      if (state[questionpath] === true && obj.dcdate) {
        if (checkSubqchoices(obj)) {
          const childObj = {
            ...obj,
            value: state[questionpath],
            dcdate: null,
            // update all the children dcdate to NULL
            subqchoices: updateAllChildrenDCDatetoNull(obj.subqchoices),
          };
          return childObj;
        }
        const childObj = {
          ...obj,
          value: state[questionpath],
          dcdate: null,
        };
        return childObj;
      }
      const childObj = {
        ...obj,
        value: state[questionpath],
      };
      return childObj;
    }
    const childObj = {
      ...obj,
      ...(checkSubqchoices(obj)
        ? {
            subqchoices: updateElementBis(obj.subqchoices, questionpath, state),
          }
        : {}),
    };
    return childObj;
  }
  return newObj;
};

/**
 * update an item with specific ID in a tree structure that has been dc
 */

export const updateDCElement = (
  obj: any,
  questionpath: any,
  date: string,
): any =>
  Array.isArray(obj)
    ? obj.map((o) => updateDCElement(o, questionpath, date))
    : obj.questionpath === questionpath
    ? { ...obj, dcdate: date } // found it and update dcdate
    : {
        ...obj,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateDCElement(obj.subqchoices, questionpath, date),
            }
          : {}),
      };

/**
 * update all Tree nodes to value false
 */

const updateAllChildrenElementToFalse = (obj: any): any =>
  Array.isArray(obj)
    ? obj.map((o) => updateAllChildrenElementToFalse(o))
    : {
        ...obj,
        value: false,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateAllChildrenElementToFalse(obj.subqchoices),
            }
          : {}),
      };

/**
 * find when a node is false, update all children node to false,
 */

export const updateFalseNodes = (obj: any): any =>
  Array.isArray(obj)
    ? obj.map((o) => updateFalseNodes(o))
    : obj.value === false || obj.value === 'false'
    ? {
        ...obj,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateAllChildrenElementToFalse(obj.subqchoices),
            }
          : {}),
      }
    : {
        ...obj,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateFalseNodes(obj.subqchoices),
            }
          : {}),
      };

/**
 * find when a controltype is STATEMENT and update value to true
 */

export const updateStatementNodes = (obj: any): any =>
  Array.isArray(obj)
    ? obj.map((o) => updateStatementNodes(o))
    : obj.controltype === statement
    ? {
        ...obj,
        value: 'true',
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateStatementNodes(obj.subqchoices),
            }
          : {}),
      }
    : {
        ...obj,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateStatementNodes(obj.subqchoices),
            }
          : {}),
      };
/**
 * Election Addendum — stamp statement nodes AND overlay addendum inputs.
 * Overlay inputs (cssclass === 'addendum') hold pasted patient data (dx, meds,
 * name) in questionlabel; their value is 'false' by default. Stamping them
 * keeps the rows in observation's ANSWERED view so print includes them.
 * Signatures, checkboxes, and typed inputs are left untouched.
 */
export const updateElectionAddendumNodes = (obj: any): any =>
  Array.isArray(obj)
    ? obj.map((o) => updateElectionAddendumNodes(o))
    : obj.controltype === statement || obj.cssclass === 'addendum'
    ? {
        ...obj,
        value: 'true',
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateElectionAddendumNodes(obj.subqchoices),
            }
          : {}),
      }
    : {
        ...obj,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateElectionAddendumNodes(obj.subqchoices),
            }
          : {}),
      };

/**
 * update all Tree nodes to dcdate
 */

const updateAllChildrenDCElement = (obj: any, date: string): any =>
  Array.isArray(obj)
    ? obj.map((o) => updateAllChildrenDCElement(o, date))
    : {
        ...obj,
        dcdate: date,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateAllChildrenDCElement(obj.subqchoices, date),
            }
          : {}),
      };

/**
 * update all nodes with null dcdate
 */

const updateAllChildrenDCDatetoNull = (obj: any): any =>
  Array.isArray(obj)
    ? obj.map((o) => updateAllChildrenDCDatetoNull(o))
    : {
        ...obj,
        dcdate: null,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateAllChildrenDCDatetoNull(obj.subqchoices),
            }
          : {}),
      };

/**
 * find when a node dcdate is true and update all children with dc date
 */

export const updateChildDCNodes = (obj: any, date: string): any =>
  // the first one if the main array and dcdate is null for those [{Related},{Goal},{Interventions}]
  Array.isArray(obj)
    ? // each object - main 3 objects - Related - Goal - Interventions
      obj.map((o) => updateChildDCNodes(o, date))
    : obj.dcdate !== null
    ? {
        ...obj,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateAllChildrenDCElement(obj.subqchoices, date),
            }
          : {}),
      }
    : {
        ...obj,
        ...(checkSubqchoices(obj)
          ? {
              subqchoices: updateChildDCNodes(obj.subqchoices, date),
            }
          : {}),
      };

/**
 * delete items that have been unselected
 */

const deleteElement = (obj: any, questionpath: string): any => {
  Object.keys(obj[questionpath]).length &&
    Object.keys(obj[questionpath]).forEach((childPath: string) => {
      if (obj[childPath]) {
        deleteElement(obj, childPath);
        delete obj[childPath];
      }
    });
  delete obj[questionpath];
  return obj;
};

export const unselectElement = (obj: any, questionpath: string): any => {
  obj[questionpath] = mapValues(obj[questionpath], () => false);
  deleteElement(obj, questionpath);
};

/**
 * unselect Id
 */

export const unselectId = (
  updatedArray: any,
  depthLevel: number,
  questionpath: string,
): any => {
  // for all level downs, splice it if includes questionpath
  for (
    let level: number = depthLevel + 1;
    level < updatedArray.length;
    level++
  ) {
    const hasLevel = updatedArray[level] && updatedArray[level].length;
    if (hasLevel) {
      const toRemove: any = [];
      updatedArray[level].forEach((path: string) => {
        if (path.includes(questionpath)) {
          toRemove.push(path);
        }
      });
      toRemove.length &&
        remove(updatedArray[level], (n) => toRemove.includes(n));
    }
  }
  return updatedArray;
};

/**
 * clean submitted state
 */

export const cleanSubmittedState = (state: any, path: string) => {
  // delete future children
  const newPath = `${path}->`;
  const keys = Object.keys(state);
  keys.forEach((key) => {
    if (key.includes(newPath)) {
      delete state[key];
    }
  });
  return state;
};

/**
 * find radio parent
 */

export const getRadioParent = (parseddata: any, parentQuestionPath: string) => {
  const ids = parentQuestionPath.split('->');
  // if there is length
  // we assume first items cannot be radio as no parent component
  const steps = ids.length - 1;
  if (steps > 0) {
    // initial ID
    const initialID = `${ids[0]}->${ids[1]}`;
    // first item is not through subqchoices
    const initialObj = parseddata.filter(
      (data: any) => data.questionpath === initialID,
    );

    const getChildPath = (id: string, level: number, obj: any): any => {
      const newId = `${id}->${ids[level]}`;
      const recObj = obj[0].subqchoices.filter(
        (data: any) => data.questionpath === newId,
      );
      if (level < steps) {
        return getChildPath(newId, level + 1, recObj);
      }
      return recObj[0];
    };

    if (steps > 1) {
      const newLevel = 2;
      return getChildPath(initialID, newLevel, initialObj);
    }
    return initialObj[0];
  }
  return null;
};

/**
 * find all controltypes in the tree structure
 */

function flatten(ops: any, n: any) {
  ops.push(n.controltype);
  if (checkSubqchoices(n)) {
    [].reduce.call(n.subqchoices, flatten, ops);
  }
  return ops;
}

// const node = JSON.parse(option.formdefinition);

export const getAllTypes = (node: any) => node.reduce(flatten, []);

/**
 * creates GolbalLabelState for the read only version
 */

export const createLabelState = (data: any, initseed: any) =>
  data
    ? data.reduce((seed: any, val: any) => {
        seed[val.questionpath] = val.questionlabel;
        if (checkSubqchoices(val)) {
          createLabelState(val.subqchoices, seed);
        }

        return seed;
      }, initseed as Record<string, Record<string, boolean>>)
    : null;

export const createGolbalLabelState = (data: any) => createLabelState(data, {});
/**
 * updateWhoStructure for the comma in read only mode
 */
export const updateWhoStructure = (obj: any): any =>
  Array.isArray(obj) && obj[0]
    ? obj.map((o) => updateWhoStructure(o))
    : obj
    ? obj.questionlabel === 'Who'
      ? {
          ...obj,
          ...(checkSubqchoices(obj)
            ? {
                subqchoices: updateWho(obj.subqchoices),
              }
            : {}),
        }
      : {
          ...obj,
          ...(checkSubqchoices(obj)
            ? {
                subqchoices: updateWhoStructure(obj.subqchoices),
              }
            : {}),
        }
    : {};
/**
 * create a form submission from fallback data and submit state
 */
export const createAutoSaveForm = (
  previousformsubmission: string,
  submitState: string,
): any => {
  const cleanFormData = previousformsubmission
    ? cleanFormSubmission(previousformsubmission)
    : null;
  if (cleanFormData) {
    const parsedData = JSON.parse(cleanFormData);
    if (Array.isArray(parsedData)) {
      const updatedData = updateStructure(parsedData);
      let newData = cloneDeep(updatedData);
      const submitGroupBoxState = JSON.parse(submitState);
      // need to update the controltype STATEMENT value to true for backend to persist the data
      newData = updateStatementNodes(newData);

      // **** Update answers ****
      // for each key in submitGroupBoxState, find it in updatedData and update value of items
      Object.keys(submitGroupBoxState).forEach((key) => {
        newData = updateElementBis(newData, key, submitGroupBoxState);
      });
      // update children to false if parent element is false- EDIT version
      newData = updateFalseNodes(newData);

      const stringifyData = JSON.stringify(newData);
      return stringifyData;
    }
    return null;
  }
  return null;
};

/**
 * UI ORIENTED ANSWERS DISPLAY
 */
// get number of required questions
// ****** NOT USED ******
export const getReqCount = (state: { initCount: number; ansQuest: any }) => {
  // const ansQuest = Object.keys(state.ansQuest).length;
  // const { initCount } = state;
  // if (ansQuest) {
  //   const count = initCount - ansQuest;
  //   return count;
  // }
  // return initCount;
};

const updateWho = (arr: any): any => {
  const lastIndex = arr.length - 1;
  const lastItem = arr[lastIndex];
  lastItem.lastItem = true;
  return arr;
};

const whoItemsUS = ['Patient', 'Caregiver(s)', 'Family', 'Facility', 'Other'];
const whoItemsES = [
  'Paciente',
  'Cuidador(es)',
  'Familia',
  'Instalación',
  'Otros',
];
const goalTextUS = 'Goal will be achieved within';
const goalTextES = 'Meta se logrará dentro de';
const whoTextUS = 'Who';
const whoTextES = 'Quién';
const goalSummaryUS = 'Goals';
const goalSummaryES = 'Objetivos';

export const getReadOnlySelectText = (input: any, state: any) => {
  if (input.questionlabel === whoTextUS && input.nooutcome) {
    const label = state[input.parentquestionpath] || 'Goal';
    return `${label}: `;
  }
  if (input.questionlabel === whoTextES && input.nooutcome) {
    const label = state[input.parentquestionpath] || 'Objetivos';
    return `${label}: `;
  }
  if (input.questionlabel === whoTextUS || input.questionlabel === whoTextES) {
    return null;
  }
  if (input.questionlabel === goalTextUS) {
    const label = state[input.parentquestionpath] || 'Goal';
    return `: ${label} will be achieved within`;
  }
  if (input.questionlabel === goalTextES) {
    const label = state[input.parentquestionpath] || 'Meta';
    return `: ${label} se logrará dentro de`;
  }
  if (whoItemsUS.includes(input.questionlabel) && input.lastItem) {
    return `${input.questionlabel}`;
  }
  if (whoItemsUS.includes(input.questionlabel)) {
    return `${input.questionlabel}, `;
  }
  if (whoItemsES.includes(input.questionlabel) && input.lastItem) {
    return `${input.questionlabel}`;
  }
  if (whoItemsES.includes(input.questionlabel)) {
    return `${input.questionlabel}, `;
  }
  return input.questionlabel;
};

// if top button sub form has been clicked
export const hasBeenClicked = (state: any, key: string) => {
  const submittedstate = Object.keys(state);
  const isOK = (path: string) => path.includes(key);
  return submittedstate.some(isOK);
};

/**
 * check what UI should it be , form or button
 */
export const isTitleUI = (data: [any]) => {
  if (data && data.length) {
    const isTitle =
      data.length > 1 && data.every((x: any) => x.controltype === 'label');
    return isTitle;
  }
  return false;
};
